<template>
  <div
    class="card-canvas"
    @dragover.prevent
    @drop="onDrop"
  >
    <div v-if="fields.length === 0" class="empty-canvas">
      <div class="empty-icon">
        <i class="bx bx-plus-circle"></i>
      </div>
      <h5>Comece a construir sua ficha</h5>
      <p>Arraste campos da barra lateral ou clique neles para adicionar</p>
    </div>

    <draggable
      v-else
      v-model="localFields"
      :group="{ name: 'fields', pull: false, put: true }"
      handle=".drag-handle"
      animation="200"
      ghost-class="ghost-field"
      @end="onReorder"
    >
      <div
        v-for="field in localFields"
        :key="field.id || field._tempId"
        class="field-card"
        :class="{
          'selected': isSelected(field),
          'is-standard': isStandardField(field),
          'is-form-field': isFormField(field),
          'is-internal': isInternalField(field),
          'is-rc': isRegistrationControlField(field)
        }"
        @click="selectField(field)"
      >
        <!-- Drag Handle & Label Row -->
        <div class="field-header">
          <div class="drag-handle">
            <i class="bx bx-grid-vertical"></i>
          </div>
          <div class="field-label-row">
            <i :class="getFieldIcon(field)" class="field-icon-small"></i>
            <span class="field-label">{{ getFieldLabel(field) }}</span>
            <span v-if="isStandardField(field)" class="badge badge-warning">Padrão</span>
            <span v-if="isFormField(field)" class="badge badge-info">Formulário</span>
            <span v-if="isInternalField(field)" class="badge badge-secondary">Interno</span>
            <span v-if="isRegistrationControlField(field)" class="badge badge-rc">Ctrl. Inscrição</span>
            <span v-if="isCompanionField(field)" class="badge badge-companion">Acompanhante</span>
            <span v-if="field.required" class="badge badge-required">Obrigatório</span>
          </div>
          <div class="field-actions">
            <button
              type="button"
              class="btn-action"
              @click.stop="editField(field)"
              title="Editar"
            >
              <i class="bx bx-edit-alt"></i>
            </button>
            <button
              type="button"
              class="btn-action btn-delete"
              @click.stop="removeField(field)"
              title="Remover"
            >
              <i class="bx bx-trash"></i>
            </button>
          </div>
        </div>

        <!-- Field Preview -->
        <div class="field-preview">
          <!-- Text Input Preview -->
          <template v-if="isTextType(field)">
            <input
              type="text"
              class="preview-input"
              :placeholder="getFieldPlaceholder(field)"
              disabled
            />
          </template>

          <!-- Textarea Preview -->
          <template v-else-if="isTextareaType(field)">
            <textarea
              class="preview-textarea"
              :placeholder="getFieldPlaceholder(field)"
              rows="3"
              disabled
            ></textarea>
          </template>

          <!-- Select/Dropdown Preview -->
          <template v-else-if="isSelectType(field)">
            <div class="preview-select">
              <span>{{ getFieldPlaceholder(field) }}</span>
              <i class="bx bx-chevron-down"></i>
            </div>
            <!-- Show options if available -->
            <div v-if="hasOptions(field)" class="preview-options">
              <div
                v-for="(opt, idx) in getFieldOptions(field).slice(0, 4)"
                :key="idx"
                class="preview-option"
              >
                <span class="option-bullet" :style="{ backgroundColor: getOptionColor(idx) }"></span>
                {{ getOptionLabel(opt) }}
              </div>
              <div v-if="getFieldOptions(field).length > 4" class="preview-option text-muted">
                +{{ getFieldOptions(field).length - 4 }} mais opções
              </div>
            </div>
          </template>

          <!-- Radio Preview -->
          <template v-else-if="isRadioType(field)">
            <div class="preview-radio-group">
              <div
                v-for="(opt, idx) in getFieldOptions(field).slice(0, 4)"
                :key="idx"
                class="preview-radio"
              >
                <span class="radio-circle"></span>
                {{ getOptionLabel(opt) }}
              </div>
            </div>
          </template>

          <!-- Checkbox Preview -->
          <template v-else-if="isCheckboxType(field)">
            <div class="preview-checkbox">
              <span class="checkbox-box"></span>
              <span>{{ getCheckboxLabel(field) }}</span>
            </div>
          </template>

          <!-- Date Preview -->
          <template v-else-if="isDateType(field)">
            <div class="preview-date">
              <i class="bx bx-calendar"></i>
              <span>{{ getFieldPlaceholder(field) }}</span>
            </div>
          </template>

          <!-- File Upload Preview -->
          <template v-else-if="isFileType(field)">
            <div class="preview-file">
              <i class="bx bx-cloud-upload"></i>
              <span>Arraste o arquivo ou clique para fazer upload</span>
            </div>
          </template>

          <!-- Default Preview -->
          <template v-else>
            <input
              type="text"
              class="preview-input"
              :placeholder="getFieldPlaceholder(field)"
              disabled
            />
          </template>
        </div>

        <!-- Help Text -->
        <div v-if="field.help_text" class="field-help">
          {{ field.help_text }}
        </div>

        <!-- Field Meta Info -->
        <div class="field-meta-info">
          <span v-if="field.layout" class="meta-item">
            <i class="bx bx-layout"></i>
            Grid: {{ field.layout.cols || 12 }}/12
          </span>
          <span v-if="field.field_source === 'form_field' && field.form_field" class="meta-item">
            <i class="bx bx-collection"></i>
            {{ getFormName(field) }}
          </span>
        </div>
      </div>
    </draggable>
  </div>
</template>

<script>
import draggable from 'vuedraggable'

export default {
  name: 'CardCanvas',
  components: {
    draggable
  },
  props: {
    fields: {
      type: Array,
      default: function () { return [] }
    },
    selectedField: {
      type: Object,
      default: null
    }
  },
  data () {
    return {
      localFields: [],
      optionColors: ['#4285f4', '#34a853', '#fbbc04', '#ea4335', '#9c27b0', '#00bcd4']
    }
  },
  watch: {
    fields: {
      immediate: true,
      deep: true,
      handler (val) {
        this.localFields = val ? [...val] : []
      }
    }
  },
  methods: {
    isSelected (field) {
      if (!this.selectedField) return false

      if (this.selectedField.id && field.id && this.selectedField.id === field.id) {
        return true
      }

      if (this.selectedField._tempId && field._tempId && this.selectedField._tempId === field._tempId) {
        return true
      }

      return false
    },

    isStandardField (field) {
      return field.field_source === 'standard'
    },

    isFormField (field) {
      return field.field_source === 'form_field'
    },

    isCompanionField (field) {
      const cfg = field.field_config || {}
      if (cfg.display_for === 'companion') return true
      return field.field_source === 'form_field' &&
        field.form_field && field.form_field.answer_owner === 'companion'
    },

    isInternalField (field) {
      return field.field_source === 'internal'
    },

    isRegistrationControlField (field) {
      return field.field_source === 'registration_control'
    },

    getFieldLabel (field) {
      if (field.label) return field.label

      if (this.isStandardField(field)) {
        var labels = {
          name: 'Nome Completo',
          badge_name: 'Nome para Crachá',
          email: 'E-mail',
          cpf: 'CPF',
          phone: 'Telefone',
          gender: 'Gênero',
          birth_date: 'Data de Nascimento',
          address: 'Endereço',
          city: 'Cidade',
          state: 'Estado',
          zip_code: 'CEP',
          country: 'País'
        }
        return labels[field.standard_field_name] || field.standard_field_name
      }

      if (this.isFormField(field) && field.form_field) {
        return field.form_field.label
      }

      // Campos internos: verificar field_config.label
      if (this.isInternalField(field) && field.field_config) {
        return field.field_config.label || field.field_config.name || 'Campo Interno'
      }

      // Campos de controle de inscrição
      if (field.field_source === 'registration_control' && field.field_config) {
        return field.field_config.label || field.field_config.name || 'Ctrl. Inscrição'
      }

      return 'Campo'
    },

    getFieldIcon (field) {
      if (field.icon) return field.icon

      if (this.isStandardField(field)) {
        var icons = {
          name: 'bx bx-user',
          email: 'bx bx-envelope',
          cpf: 'bx bx-id-card',
          phone: 'bx bx-phone',
          gender: 'bx bx-user-circle',
          birth_date: 'bx bx-calendar',
          address: 'bx bx-map',
          city: 'bx bx-building',
          state: 'bx bx-map-pin',
          zip_code: 'bx bx-map-alt',
          country: 'bx bx-world'
        }
        return icons[field.standard_field_name] || 'bx bx-text'
      }

      if (this.isFormField(field) && field.form_field && field.form_field.field_type) {
        return field.form_field.field_type.icon || 'bx bx-text'
      }

      if (this.isInternalField(field) && field.field_type) {
        return field.field_type.icon || 'bx bx-text'
      }

      return 'bx bx-text'
    },

    getFieldPlaceholder (field) {
      if (field.placeholder) return field.placeholder

      if (this.isFormField(field) && field.form_field) {
        return field.form_field.placeholder || 'Digite aqui...'
      }

      return 'Digite aqui...'
    },

    getFormName (field) {
      if (field.form_field && field.form_field.form_step && field.form_field.form_step.form) {
        return field.form_field.form_step.form.name
      }
      return 'Formulário'
    },

    isTextType (field) {
      var textTypes = ['text', 'email', 'phone', 'cpf', 'cnpj', 'cpf_cnpj', 'cep', 'rg', 'number', 'password', 'hidden']

      if (this.isStandardField(field)) {
        var standardTextFields = ['name', 'email', 'cpf', 'phone', 'address', 'city', 'zip_code']
        return standardTextFields.includes(field.standard_field_name)
      }

      if (this.isFormField(field) && field.form_field && field.form_field.field_type) {
        return textTypes.includes(field.form_field.field_type.name)
      }

      if (this.isInternalField(field) && field.field_type) {
        return textTypes.includes(field.field_type.name)
      }

      return false
    },

    isTextareaType (field) {
      if (this.isFormField(field) && field.form_field && field.form_field.field_type) {
        return field.form_field.field_type.name === 'textarea'
      }

      if (this.isInternalField(field) && field.field_type) {
        return field.field_type.name === 'textarea'
      }

      return false
    },

    isSelectType (field) {
      var selectTypes = ['select', 'multiselect', 'state']

      if (this.isStandardField(field)) {
        return ['gender', 'state', 'country'].includes(field.standard_field_name)
      }

      if (this.isFormField(field) && field.form_field && field.form_field.field_type) {
        return selectTypes.includes(field.form_field.field_type.name)
      }

      if (this.isInternalField(field) && field.field_type) {
        return selectTypes.includes(field.field_type.name)
      }

      return false
    },

    isRadioType (field) {
      if (this.isFormField(field) && field.form_field && field.form_field.field_type) {
        return field.form_field.field_type.name === 'radio'
      }

      if (this.isInternalField(field) && field.field_type) {
        return field.field_type.name === 'radio'
      }

      return false
    },

    isCheckboxType (field) {
      if (this.isFormField(field) && field.form_field && field.form_field.field_type) {
        return field.form_field.field_type.name === 'checkbox' || field.form_field.field_type.name === 'terms'
      }

      if (this.isInternalField(field) && field.field_type) {
        return field.field_type.name === 'checkbox' || field.field_type.name === 'terms'
      }

      return false
    },

    isDateType (field) {
      var dateTypes = ['date', 'time', 'datetime', 'daterange', 'birthdate']

      if (this.isStandardField(field)) {
        return field.standard_field_name === 'birth_date'
      }

      if (this.isFormField(field) && field.form_field && field.form_field.field_type) {
        return dateTypes.includes(field.form_field.field_type.name)
      }

      if (this.isInternalField(field) && field.field_type) {
        return dateTypes.includes(field.field_type.name)
      }

      return false
    },

    isFileType (field) {
      if (this.isFormField(field) && field.form_field && field.form_field.field_type) {
        return field.form_field.field_type.name === 'file' || field.form_field.field_type.name === 'image'
      }

      if (this.isInternalField(field) && field.field_type) {
        return field.field_type.name === 'file' || field.field_type.name === 'image'
      }

      return false
    },

    hasOptions (field) {
      var options = this.getFieldOptions(field)
      return options && options.length > 0
    },

    getFieldOptions (field) {
      if (field.options && field.options.length > 0) {
        return field.options
      }

      if (this.isFormField(field) && field.form_field && field.form_field.options) {
        return field.form_field.options
      }

      if (this.isStandardField(field)) {
        if (field.standard_field_name === 'gender') {
          return [
            { label: 'Masculino', value: 'male' },
            { label: 'Feminino', value: 'female' },
            { label: 'Outro', value: 'other' }
          ]
        }
      }

      return []
    },

    getOptionLabel (opt) {
      if (typeof opt === 'string') return opt
      return opt.label || opt.value || opt
    },

    getCheckboxLabel (field) {
      var options = this.getFieldOptions(field)
      if (options && options[0]) {
        return this.getOptionLabel(options[0])
      }
      return 'Aceito os termos'
    },

    getOptionColor (index) {
      return this.optionColors[index % this.optionColors.length]
    },

    selectField (field) {
      this.$emit('select-field', field)
    },

    editField (field) {
      this.$emit('edit-field', field)
    },

    removeField (field) {
      this.$emit('remove-field', field.id || field._tempId)
    },

    onReorder () {
      this.$emit('reorder-fields', this.localFields)
    },

    onDrop (event) {
      var data = event.dataTransfer.getData('field')
      if (data) {
        try {
          var fieldData = JSON.parse(data)
          this.$emit('add-field', fieldData)
        } catch (e) {
          console.error('Invalid drop data', e)
        }
      }
    }
  }
}
</script>

<style scoped>
.card-canvas {
  min-height: 400px;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  padding: 24px;
}

.empty-canvas {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 350px;
  text-align: center;
}

.empty-icon {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: #f0f4ff;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
}

.empty-icon i {
  font-size: 32px;
  color: #556ee6;
}

.empty-canvas h5 {
  font-size: 16px;
  font-weight: 600;
  color: #495057;
  margin-bottom: 8px;
}

.empty-canvas p {
  font-size: 14px;
  color: #6c757d;
  margin: 0;
}

/* Field Card */
.field-card {
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  margin-bottom: 12px;
  padding: 16px;
  cursor: pointer;
  transition: all 0.15s ease;
}

.field-card:hover {
  border-color: #d0d5dd;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.field-card.selected {
  border-color: #556ee6;
  box-shadow: 0 0 0 3px rgba(85, 110, 230, 0.15);
}

.field-card.is-standard {
  border-left: 3px solid #ffc107;
}

.field-card.is-form-field {
  border-left: 3px solid #17a2b8;
}

.field-card.is-internal {
  border-left: 3px solid #6c757d;
}

.field-card.is-rc {
  border-left: 3px solid #e67e22;
}

.badge-rc {
  background: #fef3e2;
  color: #e67e22;
}

.badge-companion {
  background: #e0e7ff;
  color: #4f46e5;
}

/* Field Header */
.field-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
}

.drag-handle {
  cursor: grab;
  color: #adb5bd;
  padding: 4px;
}

.drag-handle:hover {
  color: #556ee6;
}

.drag-handle:active {
  cursor: grabbing;
}

.field-label-row {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 8px;
}

.field-icon-small {
  font-size: 18px;
  color: #556ee6;
}

.field-label {
  font-weight: 500;
  font-size: 14px;
  color: #344054;
}

.badge {
  font-size: 10px;
  font-weight: 500;
  padding: 2px 8px;
  border-radius: 4px;
}

.badge-warning {
  background: #fff3cd;
  color: #856404;
}

.badge-info {
  background: #d1ecf1;
  color: #0c5460;
}

.badge-secondary {
  background: #e2e3e5;
  color: #383d41;
}

.badge-required {
  background: #fee2e2;
  color: #dc2626;
}

.field-actions {
  display: flex;
  gap: 4px;
  opacity: 0;
  transition: opacity 0.15s;
}

.field-card:hover .field-actions {
  opacity: 1;
}

.btn-action {
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  border-radius: 4px;
  color: #6c757d;
  cursor: pointer;
}

.btn-action:hover {
  background: #f3f4f6;
}

.btn-action.btn-delete:hover {
  background: #fee2e2;
  color: #dc2626;
}

/* Field Preview */
.field-preview {
  margin-bottom: 8px;
}

.preview-input {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  font-size: 14px;
  color: #9ca3af;
  background: #f9fafb;
}

.preview-textarea {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  font-size: 14px;
  color: #9ca3af;
  background: #f9fafb;
  resize: none;
}

.preview-select {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  font-size: 14px;
  color: #9ca3af;
  background: #f9fafb;
}

.preview-options {
  margin-top: 8px;
  padding-left: 8px;
}

.preview-option {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 0;
  font-size: 13px;
  color: #6b7280;
}

.option-bullet {
  width: 8px;
  height: 8px;
  border-radius: 50%;
}

.preview-radio-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.preview-radio {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #374151;
}

.radio-circle {
  width: 16px;
  height: 16px;
  border: 2px solid #d1d5db;
  border-radius: 50%;
}

.preview-checkbox {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #374151;
}

.checkbox-box {
  width: 16px;
  height: 16px;
  border: 2px solid #d1d5db;
  border-radius: 4px;
}

.preview-date {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  font-size: 14px;
  color: #9ca3af;
  background: #f9fafb;
}

.preview-file {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 24px;
  border: 2px dashed #e5e7eb;
  border-radius: 6px;
  background: #f9fafb;
  color: #9ca3af;
}

.preview-file i {
  font-size: 24px;
  margin-bottom: 8px;
}

/* Help Text */
.field-help {
  font-size: 12px;
  color: #6b7280;
  margin-top: 4px;
  margin-bottom: 8px;
}

/* Field Meta Info */
.field-meta-info {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 11px;
  color: #6c757d;
  padding-top: 8px;
  border-top: 1px solid #f3f4f6;
}

.meta-item {
  display: flex;
  align-items: center;
  gap: 4px;
}

.meta-item i {
  font-size: 13px;
}

/* Ghost */
.ghost-field {
  opacity: 0.5;
  background: #f3f4f6;
}
</style>
